import { Grid, Skeleton } from "@mui/material";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { useScope } from "~/hooks/useScope";
import { groupByExceptionType } from "~/pages/findings/Exceptions/utils";
import { ExceptionFindingsTable } from "~/components/ExceptionFindingsTable/ExceptionFindingsTable";
import { Asset } from "~/pages/inventory/asset/types";
import { ExceptionFindingsTableScope } from "~/components/ExceptionFindingsTable/exceptionFindigsTableTypes";
import { ExtensionHistoryTable } from "./ExtensionHistoryTable";
import { ExceptionGroupExtension } from "~/operations";

interface ExceptionGroupTablesProps {
  assetLoading?: boolean;
  exceptionGroup: ExceptionGroup;
  exceptionScope?: ExceptionFindingsTableScope;
  assets?: Asset[];
  extensions?: ExceptionGroupExtension[];
  showFindingsWhenSingle?: boolean;
}

export function ExceptionGroupTables({
  assetLoading = false,
  exceptionGroup,
  exceptionScope = "space",
  assets,
  extensions,
  showFindingsWhenSingle = false,
}: ExceptionGroupTablesProps) {
  const { spaceScope } = useScope();
  const spaceId = spaceScope?.id;

  const exceptionsCount = exceptionGroup?.exceptions?.length || 0;
  const hasAnyExceptions = exceptionsCount > 0;
  const hasMultipleExceptions = exceptionsCount > 1;
  const hasExtensions = extensions && extensions.length > 0;

  const shouldShowFindings =
    hasMultipleExceptions || (showFindingsWhenSingle && hasAnyExceptions);

  if (!shouldShowFindings && !hasExtensions) {
    return null;
  }

  const {
    spaceCveExceptions,
    spaceAdvisoryExceptions,
    spaceCheckExceptions,
    assetCveExceptions,
    assetAdvisoryExceptions,
    assetCheckExceptions,
    spaceFexExceptions,
    assetFexExceptions,
  } = groupByExceptionType(exceptionGroup);

  return (
    <Grid container spacing={3}>
      {shouldShowFindings && (
        <>
          {assetLoading && (
            <Grid size={{ xs: 12 }}>
              <Skeleton
                variant="rectangular"
                height={100}
                animation="wave"
                sx={{ borderRadius: 1 }}
              />
            </Grid>
          )}
          {Boolean(assets?.length) && !assetLoading && (
            <ExceptionFindingsTable
              findingType="asset"
              scope="space"
              findings={assets || []}
              contextProps={{ spaceId }}
            />
          )}
          {spaceCheckExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="check"
                scope={exceptionScope}
                findings={spaceCheckExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {spaceAdvisoryExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="advisory"
                scope={exceptionScope}
                findings={spaceAdvisoryExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {spaceCveExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="cve"
                scope={exceptionScope}
                findings={spaceCveExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {assetCheckExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="check"
                scope={exceptionScope}
                findings={assetCheckExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {assetAdvisoryExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="advisory"
                scope={exceptionScope}
                findings={assetAdvisoryExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {assetCveExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="cve"
                scope={exceptionScope}
                findings={assetCveExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {spaceFexExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="fex"
                scope={exceptionScope}
                findings={spaceFexExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
          {assetFexExceptions.length > 0 && spaceId && (
            <Grid size={{ xs: 12 }}>
              <ExceptionFindingsTable
                findingType="fex"
                scope={exceptionScope}
                findings={assetFexExceptions}
                contextProps={{ spaceId }}
              />
            </Grid>
          )}
        </>
      )}

      {hasExtensions && (
        <Grid size={{ xs: 12 }}>
          <ExtensionHistoryTable extensions={extensions} />
        </Grid>
      )}
    </Grid>
  );
}
